import { useCallback, useEffect } from "react";
import "./i18n";
import Routes from "../routes";
import { ThemeProvider } from "@mui/material/styles";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AxiosInterceptors } from "../routes/AxiosInterceptors";
import { useStore } from "react-redux";
import { useDesignFetcher } from "../utils/DesignsFetcherHooks";
import { useCart } from "../utils/CartHooks";
import { useCurrency } from "../utils/Currencies";
import _ from "lodash";
import "../styles/global.scss";
import { useProjectFetcher } from "../utils/ProjectsFetcherHooks";
import { useTeamFetcher } from "../utils/TeamsFetcherHooks";
import SegmentHistoryListener from "./SegmentHistoryListener";
import { useUserInfoFetcher } from "../utils/UserHooks";
import { ErrorBoundary } from "react-error-boundary";

import "@stripe/stripe-js"; //Recommended by Stripe to put the import here (see https://github.com/stripe/stripe-js)
import { useSelector } from "../common/hooks/useSelector";
import { SpecsProvider } from "../context/Specs/SpecsProvider";
import { ErrorFallback } from "../common/components/ErrorFallback/ErrorFallback";
import { useParams } from "react-router-dom";
import { theme } from "../styles/theme";
import "react-material-symbols/rounded";

const App = () => {
  const { dispatch } = useStore();
  const params = useParams();

  //When user will be logged in, userInfos will be set and valid
  //If not, then no point in calling the other APIs
  const userInfos = useSelector((state) => state.userInfo);

  //If userInfos is not set it can be because we are not logged in yet
  //or that the user is using the url directly withhout going through the login page
  //If it's the latter, so we must check if the user is logged in and set the userInfos in the store
  const { initUserInfo } = useUserInfoFetcher();
  useEffect(() => {
    initUserInfo();
  }, [dispatch, initUserInfo]);

  const { initCurrency, getCurrency } = useCurrency();
  useEffect(() => {
    initCurrency();
  }, [initCurrency]);

  const { fetchMyDesignsAndUpdateStore } = useDesignFetcher();
  const initDesigns = useCallback(async () => {
    if (!userInfos || _.isEmpty(userInfos) || !params.projectId) {
      return;
    }

    return fetchMyDesignsAndUpdateStore(params.projectId);
  }, [fetchMyDesignsAndUpdateStore, params.projectId, userInfos]);

  const { fetchMyProjectsAndUpdateStore } = useProjectFetcher();
  const initProjects = useCallback(async () => {
    if (!userInfos || _.isEmpty(userInfos)) {
      return;
    }
    return fetchMyProjectsAndUpdateStore();
  }, [fetchMyProjectsAndUpdateStore, userInfos]);

  const { fetchMyTeamsAndUpdateStore, fetchAllTeamsAndUpdateStore } =
    useTeamFetcher();
  const initTeams = useCallback(async () => {
    if (!userInfos || _.isEmpty(userInfos)) {
      return;
    }

    //Fetch all teams if designer
    if (userInfos.isDesigner) {
      await fetchAllTeamsAndUpdateStore();
    }

    return fetchMyTeamsAndUpdateStore();
  }, [fetchMyTeamsAndUpdateStore, fetchAllTeamsAndUpdateStore, userInfos]);

  const { fetchCartItemsAndUpdateStore } = useCart();
  const initCart = useCallback(async () => {
    if (!userInfos || _.isEmpty(userInfos)) {
      return;
    }

    return fetchCartItemsAndUpdateStore(getCurrency());
  }, [fetchCartItemsAndUpdateStore, userInfos, getCurrency]);

  useEffect(() => {
    initDesigns();
    initProjects();
    initTeams();
    initCart();
  }, [initDesigns, initProjects, initTeams, initCart]);

  return (
    <ThemeProvider theme={theme}>
      <SpecsProvider>
        <ErrorBoundary FallbackComponent={ErrorFallback}>
          <SegmentHistoryListener>
            <Box>
              <CssBaseline>
                <AxiosInterceptors />
                <Routes />
              </CssBaseline>
            </Box>
            <ToastContainer
              position="bottom-center"
              pauseOnFocusLoss={false}
              newestOnTop
              style={{ width: "800px" }}
            />
          </SegmentHistoryListener>
        </ErrorBoundary>
      </SpecsProvider>
    </ThemeProvider>
  );
};

export default App;
